/**
 * Splitting a consolidated trade over the consolidation's portfolios (TRADE-CONS step 4b). Pure (no
 * React), checked on its own by consolidatedTrade.check.mjs. Pedro 2026-10-30 (S4-Q3, until the
 * optimizer of step 5): "keep each security where it lives" —
 *   - a sale is split pro rata to what each portfolio holds;
 *   - a buy of a security some portfolios hold is split pro rata to where it is held;
 *   - a security no portfolio holds gets no default: it all goes to the portfolio picked for it,
 *     and with none picked nothing is split (the row stays red until one is picked).
 * Lot rules (smoke 2026-10-30, Pedro: "does not allow 500, even though it is an acceptable multiple of
 * a lot"): the consolidation's total is held to the lot rules ONCE (`adjust` on the whole); each
 * portfolio's pro-rata share is rounded down to whole lots, and the lots left over go, one at a time,
 * to the portfolio furthest below its pro-rata share that can take one (a sale: no more than it holds,
 * never leaving less than the minimum lot) — so 500 in lots of 100 stays 500.
 */
import { adjust, type LotRules, type Side } from './tradeMath';

const EPS = 1e-9;

/** The quantity before the lot rules, per portfolio (ref → quantity); null = nowhere to put it. */
export function defaultSplit(side: Side, quantity: number, held: Record<string, number>, pick: string | null):
  Record<string, number> | null {
  const total = Object.values(held).reduce((s, h) => s + Math.max(0, h), 0);
  if (total <= 0) {
    if (side === 'sell' || !pick || !(pick in held)) return null;
    return Object.fromEntries(Object.keys(held).map((p) => [p, p === pick ? quantity : 0]));
  }
  return Object.fromEntries(Object.entries(held).map(([p, h]) => [p, (quantity * Math.max(0, h)) / total]));
}

export interface PortfolioShare { quantity: number; note: string | null }

/** Each portfolio's share after its own lot rules and holding (no short sales) — used for a split
 *  typed by hand, where each portfolio's quantity is the user's. */
export function adjustSplit(side: Side, raw: Record<string, number>, held: Record<string, number>, lots: LotRules,
  typedQuantity: boolean): Record<string, PortfolioShare> {
  const out: Record<string, PortfolioShare> = {};
  for (const [p, q] of Object.entries(raw)) {
    out[p] = q > 0 ? adjust(q, side, held[p] ?? 0, lots, typedQuantity) : { quantity: 0, note: null };
  }
  return out;
}

const fmtQ = (q: number) => q.toLocaleString(undefined, { maximumFractionDigits: 6 });

/** The default split of a consolidated trade, in whole lots (see the module notes). `total.note` says
 *  how the consolidation's quantity was adjusted; a share's note says how its pro-rata part moved. */
export function splitInLots(side: Side, quantity: number, held: Record<string, number>, pick: string | null,
  lots: LotRules, typedQuantity: boolean): { shares: Record<string, PortfolioShare>; total: number; note: string | null } | null {
  const raw = defaultSplit(side, quantity, held, pick);
  if (!raw) return null;
  const heldTotal = Object.values(held).reduce((s, h) => s + Math.max(0, h), 0);
  // The whole first: rounded to the lot rules, a sale capped at what the consolidation holds.
  const whole = adjust(quantity, side, heldTotal, lots, typedQuantity);
  // Selling all the consolidation holds: each portfolio sells all it holds (no lot rule applies).
  if (side === 'sell' && whole.quantity >= heldTotal - EPS) {
    return { total: heldTotal, note: whole.note,
      shares: Object.fromEntries(Object.keys(held).map((p) => [p, { quantity: Math.max(0, held[p]), note: null }])) };
  }
  const target = whole.quantity;
  const scaled = quantity > 0 ? target / quantity : 0;
  const want = Object.fromEntries(Object.entries(raw).map(([p, q]) => [p, q * scaled]));   // pro rata of the adjusted whole
  const inc = lots.minIncrement && lots.minIncrement > 0 ? lots.minIncrement : (typedQuantity ? null : 1);
  const minPiece = lots.minPiece && lots.minPiece > 0 ? lots.minPiece : 0;
  const q: Record<string, number> = {};
  for (const [p, w] of Object.entries(want)) {
    let x = inc ? Math.floor((w + EPS) / inc) * inc : w;
    if (side === 'sell') x = Math.min(x, Math.max(0, held[p] ?? 0));
    if (x > EPS && x < minPiece - EPS) x = 0;
    // A sale must not leave less than the minimum lot behind (unless it sells everything).
    if (side === 'sell' && x > EPS && minPiece && (held[p] - x) > EPS && (held[p] - x) < minPiece - EPS) x = 0;
    q[p] = x;
  }
  // The lots left over, one at a time, to the portfolio furthest below its share that can take one.
  if (inc) {
    let left = target - Object.values(q).reduce((s, x) => s + x, 0);
    for (let guard = 0; left > EPS && guard < 100000; guard++) {
      let best: string | null = null, bestStep = 0, bestGap = -Infinity;
      for (const p of Object.keys(want)) {
        if (side === 'buy' && (held[p] ?? 0) <= 0 && Object.values(held).some((h) => h > 0)) continue;   // buys stay where it lives
        if (side === 'buy' && Object.values(held).every((h) => h <= 0) && (want[p] ?? 0) <= 0) continue;   // an unheld one: its portfolio only
        const step = q[p] > EPS ? inc : Math.ceil((Math.max(inc, minPiece) - EPS) / inc) * inc;
        if (step > left + EPS) continue;
        const after = q[p] + step;
        if (side === 'sell') {
          const rest = (held[p] ?? 0) - after;
          if (rest < -EPS || (rest > EPS && minPiece && rest < minPiece - EPS)) continue;
        }
        const gap = want[p] - q[p];
        if (gap > bestGap + EPS || (Math.abs(gap - bestGap) <= EPS && best != null && (held[p] ?? 0) > (held[best] ?? 0))) {
          best = p; bestStep = step; bestGap = gap;
        }
      }
      if (!best) break;
      q[best] += bestStep;
      left -= bestStep;
    }
  }
  const total = Object.values(q).reduce((s, x) => s + x, 0);
  const notes: string[] = [];
  if (whole.note) notes.push(whole.note);
  if (total < target - EPS) notes.push(`${fmtQ(target - total)} could not be placed in whole lots in any portfolio`);
  const shares: Record<string, PortfolioShare> = {};
  for (const p of Object.keys(want)) {
    const moved = Math.abs(q[p] - want[p]) > EPS && want[p] > EPS;
    shares[p] = { quantity: q[p], note: moved ? `${fmtQ(want[p])} pro rata → ${fmtQ(q[p])} in whole lots${inc && inc !== 1 ? ` of ${fmtQ(inc)}` : ''}` : null };
  }
  return { shares, total, note: notes.length ? notes.join('; ') : null };
}
